import React, { useEffect, useRef } from "react";
import { Box, Divider, Stack, Tooltip, Typography } from "@mui/material";
import { useTheme } from "@mui/material/styles";
import { Editor } from "@tiptap/core";
import {
  PlusIcon,
  DeleteIcon,
  UpArrowIcon,
  DownArrowIcon,
  LeftArrowIcon,
  RightArrowIcon,
} from "components/icons";
import { TableToolbarProps, TableToolbarAction } from "./tableToolbar.types";

interface ToolbarButtonProps {
  action: TableToolbarAction;
  editor: Editor;
}

const ToolbarButton: React.FC<ToolbarButtonProps> = ({ action, editor }) => {
  const theme = useTheme();
  const isDisabled = action.isDisabled?.(editor) ?? false;

  return (
    <Tooltip title={action.label} placement="top">
      <Stack
        onClick={(e) => {
          e.preventDefault();
          e.stopPropagation();
          if (!isDisabled) {
            action.action(editor);
          }
        }}
        sx={{
          cursor: isDisabled ? "not-allowed" : "pointer",
          minWidth: "32px",
          minHeight: "32px",
          borderRadius: "6px",
          padding: "4px",
          alignItems: "center",
          justifyContent: "center",
          opacity: isDisabled ? 0.5 : 1,
          backgroundColor: "transparent",
          transition: "background-color 0.15s ease",
          "&:hover": {
            backgroundColor: isDisabled
              ? "transparent"
              : action.isDanger
                ? theme.palette.error.tint
                : theme.palette.neutral["010"],
          },
          "& svg": {
            color: action.isDanger
              ? theme.palette.error.main
              : theme.palette.lightBg.medium,
          },
        }}
      >
        {action.icon}
      </Stack>
    </Tooltip>
  );
};

export const TableToolbar: React.FC<TableToolbarProps> = ({
  editor,
  onClose,
}) => {
  const theme = useTheme();
  const toolbarRef = useRef<HTMLDivElement>(null);

  const rowActions: TableToolbarAction[] = [
    {
      id: "addRowBefore",
      label: "Add row above",
      icon: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <UpArrowIcon sx={{ fontSize: 16 }} />
          <PlusIcon sx={{ fontSize: 14 }} />
        </Stack>
      ),
      action: (editor) => editor.chain().focus().addRowBefore().run(),
    },
    {
      id: "addRowAfter",
      label: "Add row below",
      icon: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <DownArrowIcon sx={{ fontSize: 16 }} />
          <PlusIcon sx={{ fontSize: 14 }} />
        </Stack>
      ),
      action: (editor) => editor.chain().focus().addRowAfter().run(),
    },
    {
      id: "deleteRow",
      label: "Delete row",
      icon: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography variant="body-small" sx={{ fontWeight: 500 }}>
            Row
          </Typography>
          <DeleteIcon sx={{ fontSize: 16 }} />
        </Stack>
      ),
      action: (editor) => editor.chain().focus().deleteRow().run(),
      isDanger: true,
    },
  ];

  const columnActions: TableToolbarAction[] = [
    {
      id: "addColumnBefore",
      label: "Add column left",
      icon: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <LeftArrowIcon sx={{ fontSize: 16 }} />
          <PlusIcon sx={{ fontSize: 14 }} />
        </Stack>
      ),
      action: (editor) => editor.chain().focus().addColumnBefore().run(),
    },
    {
      id: "addColumnAfter",
      label: "Add column right",
      icon: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <RightArrowIcon sx={{ fontSize: 16 }} />
          <PlusIcon sx={{ fontSize: 14 }} />
        </Stack>
      ),
      action: (editor) => editor.chain().focus().addColumnAfter().run(),
    },
    {
      id: "deleteColumn",
      label: "Delete column",
      icon: (
        <Stack direction="row" alignItems="center" gap={0.5}>
          <Typography variant="body-small" sx={{ fontWeight: 500 }}>
            Col
          </Typography>
          <DeleteIcon sx={{ fontSize: 16 }} />
        </Stack>
      ),
      action: (editor) => editor.chain().focus().deleteColumn().run(),
      isDanger: true,
    },
  ];

  const tableActions: TableToolbarAction[] = [
    {
      id: "deleteTable",
      label: "Delete table",
      icon: <DeleteIcon sx={{ fontSize: 18 }} />,
      action: (editor) => {
        editor.chain().focus().deleteTable().run();
        onClose();
      },
      isDanger: true,
    },
  ];

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        toolbarRef.current &&
        !toolbarRef.current.contains(event.target as Node)
      ) {
        // Check if click is inside the table
        const target = event.target as HTMLElement;
        const isInsideTable = target.closest("table");
        if (!isInsideTable) {
          onClose();
        }
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [onClose]);

  return (
    <Stack
      ref={toolbarRef}
      direction="row"
      sx={{
        p: "6px",
        border: `1px solid ${theme.palette.neutral["020"]}`,
        borderRadius: "8px",
        boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.15)",
        gap: "4px",
        bgcolor: theme.palette.common.white,
        zIndex: 20,
        alignItems: "center",
      }}
    >
      {/* Row actions */}
      {rowActions.map((action) => (
        <ToolbarButton key={action.id} action={action} editor={editor} />
      ))}

      <Divider orientation="vertical" flexItem sx={{ mx: "4px" }} />

      {/* Column actions */}
      {columnActions.map((action) => (
        <ToolbarButton key={action.id} action={action} editor={editor} />
      ))}

      <Divider orientation="vertical" flexItem sx={{ mx: "4px" }} />

      {/* Table actions */}
      {tableActions.map((action) => (
        <ToolbarButton key={action.id} action={action} editor={editor} />
      ))}
    </Stack>
  );
};
